import { z } from "zod";
import { optionalUuid } from "../lib/zod-helpers";
import { paginationSchema } from "./pagination";

/**
 * HCA Moravia — validační schémata pro komunikační log (plán §S.2.10).
 */

export const commKanalEnum = z.enum(["EMAIL", "SMS", "CALL", "INAPP", "OTHER"]);
export type CommKanal = z.infer<typeof commKanalEnum>;

export const commStavEnum = z.enum([
  "PENDING",
  "SENT",
  "DELIVERED",
  "OPENED",
  "CLICKED",
  "FAILED",
  "BOUNCED",
]);
export type CommStav = z.infer<typeof commStavEnum>;

const uuidMsg = "Neplatné UUID";

/** Ruční zápis komunikace — admin loguje hovor nebo manuální email. */
export const logEntrySchema = z.object({
  klientId: z.string().uuid(uuidMsg),
  prihlaskaId: optionalUuid(uuidMsg),
  kanal: commKanalEnum,
  stav: commStavEnum.default("SENT"),
  krok: z.string().max(50, "Krok musí být 50 znaků nebo méně").optional(),
  subjekt: z.string().max(500, "Předmět musí být 500 znaků nebo méně").optional(),
  teloPreview: z.string().max(1000, "Náhled musí být 1000 znaků nebo méně").optional(),
  providerMessageId: z.string().max(255, "ID musí být 255 znaků nebo méně").optional(),
});

/** Globální list s filtry. */
export const listCommLogSchema = paginationSchema.extend({
  klientId: optionalUuid(uuidMsg),
  prihlaskaId: optionalUuid(uuidMsg),
  kanal: commKanalEnum.optional(),
  stav: commStavEnum.optional(),
});

/** Per-klient timeline. */
export const listCommByKlientSchema = z.object({
  klientId: z.string().uuid(uuidMsg),
  limit: z.number().int().min(1).max(200).default(50),
});

/** Per-klient historie s pagination + filters — pro CRM detail view. */
export const klientCommHistorySchema = paginationSchema.extend({
  klientId: z.string().uuid(uuidMsg),
  kanal: commKanalEnum.optional(),
  stav: commStavEnum.optional(),
});

/** Poslední globální zprávy pro dashboard widget. */
export const listRecentCommSchema = z.object({
  limit: z.number().int().min(1).max(50).default(10),
});

/** Retrigger — zařadí zprávu znovu k odeslání, nový log řádek zapíše worker. */
export const retriggerCommSchema = z.object({
  logId: z.string().uuid(uuidMsg),
  duvodRetriggeru: z
    .string()
    .min(1, "Důvod retriggeru je povinný")
    .max(500, "Důvod musí být 500 znaků nebo méně"),
});

export const commLogIdSchema = z.object({
  id: z.string().uuid(uuidMsg),
});

/**
 * Ruční odeslání e-mailu klientovi z aplikace. Obsah přijde už finální
 * (UI ho může předvyplnit ze šablony přes renderTemplate); templateId je
 * jen evidenční stopa.
 */
export const sendKlientEmailSchema = z.object({
  klientId: z.string().uuid(uuidMsg),
  templateId: z.string().uuid(uuidMsg).optional(),
  predmet: z.string().min(1, "Předmět je povinný").max(255),
  teloHtml: z.string().min(1, "Text e-mailu je povinný").max(50000),
});

/**
 * Hromadný e-mail účastníkům termínu. `predmet`/`teloHtml` smí obsahovat
 * `{{proměnné}}` — renderují se až na serveru pro KAŽDÉHO příjemce zvlášť
 * (jméno klienta + kontext termínu), ne v UI při psaní.
 */
export const sendTerminEmailSchema = z.object({
  terminId: z.string().uuid(uuidMsg),
  templateId: optionalUuid(uuidMsg),
  predmet: z.string().min(1, "Předmět je povinný").max(255, "Předmět musí být 255 znaků nebo méně"),
  teloHtml: z
    .string()
    .min(1, "Text e-mailu je povinný")
    .max(50000, "Text e-mailu musí být 50000 znaků nebo méně"),
  /** Zúží příjemce na přihlášky s nedoplatkem (upomínka platby). */
  jenNezaplacene: z.boolean().default(false),
});

/** Živý seznam příjemců pro dialog hromadného e-mailu (počty + náhled). */
export const terminEmailRecipientsSchema = z.object({
  terminId: z.string().uuid(uuidMsg),
});

export type LogEntryInput = z.infer<typeof logEntrySchema>;
export type ListCommLogInput = z.infer<typeof listCommLogSchema>;
export type ListCommByKlientInput = z.infer<typeof listCommByKlientSchema>;
export type KlientCommHistoryInput = z.infer<typeof klientCommHistorySchema>;
export type ListRecentCommInput = z.infer<typeof listRecentCommSchema>;
export type RetriggerCommInput = z.infer<typeof retriggerCommSchema>;
export type CommLogIdInput = z.infer<typeof commLogIdSchema>;
export type SendKlientEmailInput = z.infer<typeof sendKlientEmailSchema>;
export type SendTerminEmailInput = z.infer<typeof sendTerminEmailSchema>;
export type TerminEmailRecipientsInput = z.infer<typeof terminEmailRecipientsSchema>;
